import { motion } from 'framer-motion';
import { Mail, Smartphone, ExternalLink } from 'lucide-react';
import React from 'react';

export default function ContactSection() {
  const contacts = [
    {
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
        </svg>
      ),
      title: 'Follow us on TikTok',
      href: 'https://www.tiktok.com/@broadway.bingo?is_from_webapp=1&sender_device=pc',
      external: true,
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email us your questions',
      subtitle: 'salinasbroadwaybingo@gmail.com',
      href: 'mailto:salinasbroadwaybingo@gmail.com',
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Reserve your seats',
      subtitle: 'Download our app',
      href: 'https://apps.apple.com/us/app/broadway-bingo/id1535452813',
    },
  ];

  return (
    <section className="bg-gradient-to-br from-gray-50 to-white py-20">
      <div className="container mx-auto px-4">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-['Josefin_Sans'] text-[#de0016] text-4xl md:text-5xl lg:text-6xl text-center mb-16 font-bold"
        >
          Contact Us
        </motion.h2>

        <div className="max-w-3xl mx-auto">
          <div className="grid gap-6">
            {contacts.map((contact, index) => (
              <motion.a
                key={index}
                href={contact.href}
                target={contact.external ? '_blank' : undefined}
                rel={contact.external ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -4 }}
                className="group bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all border border-gray-100 overflow-hidden relative"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#de0016]/5 to-[#ff1a2f]/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div className="relative flex items-center gap-6">
                  {/* Icon */}
                  <motion.div 
                    className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#de0016] to-[#ff1a2f] rounded-2xl flex items-center justify-center text-white shadow-lg"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    {contact.icon}
                  </motion.div>

                  {/* Content */}
                  <div className="flex-grow">
                    <p className="font-['Josefin_Sans'] text-xl md:text-2xl text-gray-900 font-bold mb-1 flex items-center gap-2">
                      {contact.title}
                      {contact.external && (
                        <ExternalLink className="w-5 h-5 text-[#de0016] opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </p>
                    {contact.subtitle && (
                      <p className="font-['Josefin_Sans'] text-lg text-gray-600">
                        {contact.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <motion.div
                    className="flex-shrink-0 text-[#de0016]"
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Address */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="inline-block bg-white rounded-2xl px-8 py-4 shadow-lg border border-gray-100">
            <a href='https://maps.app.goo.gl/ASxy2WBf8P3aJZPv7' className="font-['Josefin_Sans'] text-[#de0016] text-xl md:text-2xl font-bold">
              📍 19 W Bernal Dr, Salinas, CA 93906
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
